import {
  GZIP_MEDIA_TYPE,
  STRAT_MEDIA_TYPE,
  fileSuffixFor,
  gzipJson,
  portableFilename,
  portableText,
  readPortable,
} from "@strat/format";
import type { PortableDocument, PortableReadResult } from "@strat/format";

/**
 * Browser-side plumbing for the portable files: turning a document into a
 * `.strategy` / `.backtest` / `.backtest.gz` download, and a dropped file back
 * into a document. The format itself — what goes in a file and what counts as
 * valid — lives in @strat/format, so the server validates by the same rules.
 * The same code runs in the browser and in the desktop app's renderer.
 */

export interface SaveOptions {
  /** Gzip it: a `.backtest.gz`, roughly a tenth of the size. */
  compressed?: boolean;
}

/** Names a file after what's in it, so a folder of exports stays readable. */
export function filenameFor(document: PortableDocument, options: SaveOptions = {}): string {
  const suffix = fileSuffixFor(document, options);
  if (document.kind === "strategy") {
    return portableFilename(
      [document.strategy.name, document.settings?.symbol, document.settings?.timeframe],
      suffix,
    );
  }
  if (document.kind === "run") {
    const { strategyName, symbol, timeframe } = document.run;
    return portableFilename([strategyName, symbol, timeframe], suffix);
  }
  const date = document.exportedAt.slice(0, 10);
  const counts = [
    document.strategies.length > 0 ? `${document.strategies.length}-strategies` : null,
    document.runs.length > 0 ? `${document.runs.length}-runs` : null,
  ];
  return portableFilename(["strategyhive", ...counts, date], suffix);
}

/** Saves a document as a file and resolves with the name it was given. */
export async function downloadDocument(
  document: PortableDocument,
  options: SaveOptions = {},
): Promise<string> {
  const filename = filenameFor(document, options);
  // Plain files are pretty-printed: they are meant to be opened, read and
  // hand-edited. A compressed one is for size, so it skips the whitespace.
  const blob = options.compressed
    ? new Blob([(await gzipJson(document)) as BlobPart], { type: GZIP_MEDIA_TYPE })
    : new Blob([JSON.stringify(document, null, 2)], { type: STRAT_MEDIA_TYPE });
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoked on the next tick: some browsers start the download asynchronously.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return filename;
}

/**
 * Reads a dropped or picked file — plain or gzipped, whatever its name — into
 * the parsed JSON (which is what the server's import takes) and the reader's
 * verdict on it.
 */
export async function readPortableFile(
  file: Blob & { name?: string },
): Promise<{ value: unknown; read: PortableReadResult }> {
  const name = file.name ?? "That file";
  let text: string;
  try {
    text = await portableText(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return {
      value: null,
      read: { ok: false, errors: [`Couldn't read ${name} — it is damaged or not a text file.`] },
    };
  }
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch (err) {
    const why = err instanceof Error ? err.message : String(err);
    return { value: null, read: { ok: false, errors: [`${name} isn't valid JSON — ${why}`] } };
  }
  return { value, read: readPortable(value) };
}
